/**
 * viewportCommands — the registry behind the viewport right-click menu (ENG-1050).
 *
 * The menu itself knows nothing about what it can do: a feature registers a
 * command, and the menu renders whatever is registered. That is the extension
 * point the ticket asked for ("lets make it a right click menu so we can add
 * other options in the future") — a future "Look at" / "Focus here" / "Place
 * waypoint" is a `registerViewportCommand` call, not menu surgery.
 *
 * Pure logic, no THREE / no DOM / no React, so it unit-tests under `node --test`.
 * The caller (ViewportContextMenu) owns the raycast and builds the `ctx`.
 *
 * Two rules encoded here, both repo conventions rather than preferences:
 *
 *   - An unavailable command stays VISIBLE and disabled, carrying the REASON
 *     ("no navmesh loaded", "clicked 2.4 m off the navmesh"). Hiding it leaves
 *     the user with an empty menu and no idea why — the bug documented in
 *     src/lib/voiceInputState.js's header.
 *   - A `resolve` that THROWS becomes a disabled entry carrying the error, not
 *     an exception that blanks the whole menu. One broken command degrades
 *     itself, the same way a bad plugin manifest degrades only its own plugin.
 */

/** @type {Map<string, object>} id -> command */
const commands = new Map()

/**
 * Register a viewport command.
 *
 * @param {object} cmd
 * @param {string} cmd.id                stable id (also the menu's test hook)
 * @param {string} cmd.label             default menu label
 * @param {number} [cmd.order=100]       sort key; ties break on id
 * @param {(ctx:object)=>object} [cmd.resolve]  per-open availability:
 *   `{ enabled, reason?, label?, badge?, badgeTitle? }`. Omitted = always
 *   enabled with `cmd.label`.
 * @param {(ctx:object)=>any} cmd.run    invoked on click (may be async)
 * @param {*} [cmd.editor]  an opaque value the MENU knows how to render as a
 *   flyout beside the row (in practice a React component). Stored and handed
 *   back untouched — this module neither imports React nor calls it, so it stays
 *   node-testable. A command with one gets a disclosure arrow; a command without
 *   renders exactly as before.
 */
export function registerViewportCommand(cmd) {
  if (!cmd || !cmd.id) throw new Error('registerViewportCommand: id required')
  if (typeof cmd.run !== 'function') throw new Error(`registerViewportCommand: ${cmd.id} needs a run()`)
  if (commands.has(cmd.id)) throw new Error(`registerViewportCommand: duplicate id "${cmd.id}"`)
  commands.set(cmd.id, { order: 100, ...cmd })
}

/**
 * Attach an editor to an already-registered command.
 *
 * Separate from registerViewportCommand so a feature can declare its command in
 * a lib module (pure, node-testable) and contribute its UI from the component
 * layer, without either the registry or the menu naming a specific feature. The
 * menu renders whatever is attached; it does not know what walk-here is.
 */
export function setCommandEditor(id, editor) {
  const cmd = commands.get(id)
  if (!cmd) throw new Error(`setCommandEditor: no command "${id}"`)
  cmd.editor = editor
}

/** Test seam — drop a registration (no production caller). */
export function unregisterViewportCommand(id) { commands.delete(id) }

/**
 * Resolve every registered command against one right-click context.
 *
 * @param {object} ctx  whatever the menu knows about the click — for walk-here:
 *   `{ point:[x,y,z], navmesh, hasAvatar }`.
 * @returns {Array<{id,label,enabled,reason,run}>} sorted by order, then id.
 */
export function viewportCommandsFor(ctx) {
  const out = []
  for (const cmd of commands.values()) {
    let state
    if (typeof cmd.resolve !== 'function') {
      state = { enabled: true }
    } else {
      try {
        state = cmd.resolve(ctx) || { enabled: false, reason: 'unavailable' }
      } catch (err) {
        // A throwing resolve is a bug in that command — report it in place of
        // that one entry rather than losing the menu.
        console.error(`[viewportCommands] ${cmd.id}.resolve threw:`, err)
        state = { enabled: false, reason: String(err?.message ?? err) }
      }
    }
    out.push({
      id: cmd.id,
      label: state.label ?? cmd.label,
      enabled: state.enabled !== false,
      reason: state.reason ?? null,
      order: cmd.order,
      run: cmd.run,
      editor: cmd.editor ?? null,
      // A short marker the menu shows beside the label — for a command whose
      // behaviour has been changed from its shipped defaults, so the row says so
      // before it is clicked rather than after the result looks wrong. Separate
      // from `label` on purpose: appending to the label would make every caller
      // that matches on the text (specs, menu search) chase the marker.
      badge: state.badge ?? null,
      badgeTitle: state.badgeTitle ?? null,
    })
  }
  out.sort((a, b) => (a.order - b.order) || a.id.localeCompare(b.id))
  return out
}
